import { useState, useEffect, useContext, ChangeEvent } from 'react';
import { v4 as uuidv4 } from 'uuid';
import update from 'immutability-helper';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select, { SelectChangeEvent } from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import getConditionalControls from '../../../Shared/jsonSchemaUtilities/getConditionalControls';
import { WorkflowEngineContext } from '../../../WorkflowEngine/workflowEngineReducer';
import ControlState from '../../../Shared/controlState';
import JSONSchema7Properties from '../../../Shared/jSONSchema7Properties';
import {
  ConditionalRule,
  ConditionalRuleConfig,
  ConditionalRuleKind,
  RulesEngineProps
} from './types';
import getConditionalRules from './getConditionalRules';
import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import { useFormContext } from 'react-hook-form';
import getDefaultRuleConfig from './getDefaultRuleConfig';
import checkAllRulesHaveComponents from './checkAllRulesHaveComponents';
import checkInBetweenConditionalRulesAreValid from './checkInBetweenConditionalRulesAreValid';
import ValidationMsg from '../../../Shared/ValidationMsg';
import getControlSchemaProperty from '../../../Shared/jsonSchemaUtilities/getControlSchemaProperty';

export default ({
  fieldId,
  fieldName,
  isReadOnlyView,
  updateRules
}: RulesEngineProps) => {
  const { state } = useContext(WorkflowEngineContext);
  const { pages } = state;

  const formContext = useFormContext();

  const [rules, setRules] = useState<ConditionalRuleConfig[]>(
    formContext.getValues(fieldName)
  );

  const addRule = () => {
    setRules([...rules, getDefaultRuleConfig()]);
  };

  const deleteRule = (index: number) => () => {
    setRules(
      update(rules, {
        $splice: [[index, 1]]
      })
    );
  };

  const handleValue1Change =
    (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const ruleConfig: ConditionalRuleConfig = rules[index];
      ruleConfig.value1 = parseFloat(event.target.value) || 0;
      setRules(
        update(rules, {
          $splice: [[index, 1, ruleConfig]]
        })
      );
    };

  const handleValue2Change =
    (index: number) => (event: ChangeEvent<HTMLInputElement>) => {
      const ruleConfig: ConditionalRuleConfig = rules[index];
      ruleConfig.value2 = parseFloat(event.target.value) || 0;
      setRules(
        update(rules, {
          $splice: [[index, 1, ruleConfig]]
        })
      );
    };

  const handleRuleSelectChange =
    (index: number) => (event: SelectChangeEvent) => {
      const ruleConfig: ConditionalRuleConfig = rules[index];
      ruleConfig.rule = event.target.value;
      setRules(
        update(rules, {
          $splice: [[index, 1, ruleConfig]]
        })
      );
    };

  const handleShowComponentSelectChange =
    (index: number) => (event: SelectChangeEvent<string[]>) => {
      const ruleConfig: ConditionalRuleConfig = rules[index];
      ruleConfig.utilize = (event.target.value as string[])
        .filter((v) => v !== '')
        .join('|');
      setRules(
        update(rules, {
          $splice: [[index, 1, ruleConfig]]
        })
      );
    };

  const renderRuleSelect = (value: string, index: number) => {
    return (
      <FormControl
        sx={{ width: '200px' }}
        disabled={isReadOnlyView}
        size="small"
      >
        <Select value={value} onChange={handleRuleSelectChange(index)}>
          {getConditionalRules().map((rule: ConditionalRule) => {
            return (
              <MenuItem key={`Rule-${rule.kind}`} value={rule.value}>
                {rule.value}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  };

  const renderHiddenComponents = (value: string[], index: number) => {
    const conditionalControls = getConditionalControls(pages).filter((c) => {
      const controlId = getControlSchemaProperty(c, 'id') as string;
      return fieldId !== controlId;
    });

    return (
      <FormControl
        sx={{ width: '200px' }}
        disabled={isReadOnlyView}
        size="small"
      >
        <InputLabel>Show</InputLabel>
        <Select
          label="Show"
          multiple
          onChange={handleShowComponentSelectChange(index)}
          value={value}
        >
          <MenuItem value={''}>
            {conditionalControls?.length > 0
              ? 'Select component(s)'
              : 'No component(s) available'}
          </MenuItem>
          {conditionalControls?.map((ctrl: ControlState) => {
            const ctrlProperties = ctrl.schema
              ?.properties as JSONSchema7Properties;
            const ctrlId = ctrlProperties['id'].default as string;

            return (
              <MenuItem value={ctrlId} key={`Conditional-${ctrlId}`}>
                {ctrlId}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    );
  };

  const isRangeOfValues = (rule: string) => {
    const isRange = [
      ConditionalRuleKind.IS_BETWEEN,
      ConditionalRuleKind.IS_NOT_BETWEEN
    ].includes(rule as ConditionalRuleKind);

    return isRange;
  };

  const validateRules = (rules: ConditionalRuleConfig[]) => {
    const allRulesHaveComponents = checkAllRulesHaveComponents(rules);
    const allInBetweenConditionalRulesAreValid =
      checkInBetweenConditionalRulesAreValid(rules);

    let errorMsg = '';

    if (!allRulesHaveComponents) {
      errorMsg = 'All rules must have component(s) assigned.';
    }

    if (!allInBetweenConditionalRulesAreValid) {
      errorMsg =
        'All rules with a range of values must have a minimum value lower than the maximum value.';
    }

    formContext.setError(fieldName, {
      message: errorMsg
    });
  };

  useEffect(() => {
    updateRules(rules);
    setTimeout(() => validateRules(formContext.getValues(fieldName)), 0);
  }, [rules]);

  return (
    <Grid container spacing={3}>
      {rules.map((option, index) => {
        const isBetween = isRangeOfValues(option.rule);

        return (
          <Grid
            key={option.id}
            container
            item
            alignItems="center"
            justifyContent="flex-start"
            spacing={3}
          >
            <Grid item xs="auto">
              {'If value'}
            </Grid>
            <Grid item xs="auto">
              {renderRuleSelect(option.rule, index)}
            </Grid>
            <Grid item xs="auto">
              <Stack sx={{ width: '75px' }}>
                <TextField
                  fullWidth
                  id={`rule-${uuidv4()}-${index}-value1`}
                  size="small"
                  value={option.value1}
                  type="number"
                  onChange={handleValue1Change(index)}
                  disabled={isReadOnlyView}
                />
              </Stack>
            </Grid>
            <Grid item xs="auto">
              <Stack sx={{ width: '26px' }}>{isBetween && `and`}</Stack>
            </Grid>
            <Grid item xs="auto">
              <Stack sx={{ width: '75px' }}>
                {isBetween && (
                  <TextField
                    fullWidth
                    id={`rule-${uuidv4()}-${index}-value2`}
                    size="small"
                    value={option.value2}
                    type="number"
                    onChange={handleValue2Change(index)}
                    disabled={isReadOnlyView}
                  />
                )}
              </Stack>
            </Grid>

            <Grid item xs="auto">
              {renderHiddenComponents(option.utilize?.split('|'), index)}
            </Grid>
            <Grid item xs="auto">
              <IconButton
                sx={{ padding: '3px' }}
                onClick={deleteRule(index)}
                disabled={isReadOnlyView}
              >
                <DeleteIcon />
              </IconButton>
            </Grid>
          </Grid>
        );
      })}
      <Grid item xs={12}>
        <ValidationMsg
          fieldError={formContext.getFieldState(fieldName)?.error}
        />
      </Grid>
      <Grid item xs={12}>
        <Button
          onClick={addRule}
          style={{
            fontSize: '14px',
            display: 'flex',
            justifyContent: 'flex-start',
            maxWidth: '180px'
          }}
          size="small"
          startIcon={<AddIcon />}
          disabled={isReadOnlyView}
        >
          Add rule
        </Button>
      </Grid>
    </Grid>
  );
};
